"use strict";

// Per-reviewer QA statuses, as the backend reports them in `qaStatus`
// (language → status) on the reviewer's QA Review rows.
export const QA_REVIEW_ROLE = "performancedirectionversions";

export const QA_STATUS = Object.freeze({
    AVAILABLE: "available",
    IN_PROGRESS: "in_progress",
    SUBMITTED: "submitted",
    RETURNED: "returned",
});

export const QA_STATUS_LABELS = {
    [QA_STATUS.AVAILABLE]: "Available",
    [QA_STATUS.IN_PROGRESS]: "In progress",
    [QA_STATUS.SUBMITTED]: "Submitted",
    [QA_STATUS.RETURNED]: "Returned",
};

export const QA_STATUS_COLORS = {
    [QA_STATUS.AVAILABLE]: "default",
    [QA_STATUS.IN_PROGRESS]: "info",
    [QA_STATUS.SUBMITTED]: "success",
    [QA_STATUS.RETURNED]: "warning",
};

export const qaStatusLabel = (status) =>
    QA_STATUS_LABELS[status] || QA_STATUS_LABELS[QA_STATUS.AVAILABLE];

export const qaStatusOf = (row, language) =>
    row?.qaStatus?.[language] || QA_STATUS.AVAILABLE;

const isQAReviewRow = (row) =>
    row?.type !== "audiobook" && row?.role === QA_REVIEW_ROLE;

// The languages the reviewer can still work on: everything not submitted.
export const workableLanguages = (row) => {
    const languages = row?.finalLang || [];
    if (!isQAReviewRow(row)) return languages;
    return languages.filter(
        (language) => qaStatusOf(row, language) !== QA_STATUS.SUBMITTED
    );
};

// QA Review rows drop off once every language is submitted.
export const isOnQADashboard = (row) =>
    !isQAReviewRow(row) ||
    !(row.finalLang || []).length ||
    workableLanguages(row).length > 0;

// Languages the reviewer has submitted on a QA Review row.
export const submittedLanguages = (row) =>
    isQAReviewRow(row)
        ? (row.finalLang || []).filter(
            (language) => qaStatusOf(row, language) === QA_STATUS.SUBMITTED
        )
        : [];

// The header's role switcher: the languages it offers for `role`, leaving out
// submitted QA reviews, and whether to show the role at all.
export const switcherLanguages = (role, finalLang = [], qaSubmitted = []) =>
    role === QA_REVIEW_ROLE
        ? finalLang.filter((language) => !qaSubmitted.includes(language))
        : finalLang;

export const isRoleInSwitcher = (role, finalLang = [], qaSubmitted = []) =>
    role !== QA_REVIEW_ROLE ||
    !finalLang.length ||
    switcherLanguages(role, finalLang, qaSubmitted).length > 0;

// Review times are ISO strings; "" when missing or unreadable.
export const formatQADate = (iso) => {
    const date = iso ? new Date(iso) : null;
    return date && !Number.isNaN(date.getTime())
        ? date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
        : "";
};
